import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { FiX, FiSearch } from 'react-icons/fi';
import { stockInAPI, productsAPI } from '../../services/api';

const StockInForm = ({ record, onClose, onSuccess }) => {
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    product_id: '',
    quantity: '',
    location: 'Office',
    unit_price: '',
    supplier_name: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  // Searchable product dropdown state
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const productDropdownRef = useRef(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (record) {
      setFormData({
        product_id: record.product_id || '',
        quantity: record.quantity || '',
        location: record.location || 'Office',
        unit_price: record.unit_price || '',
        supplier_name: record.supplier_name || '',
        invoice_number: record.invoice_number || '',
        invoice_date: record.invoice_date ? record.invoice_date.split('T')[0] : new Date().toISOString().split('T')[0],
        notes: record.notes || ''
      });
    }
  }, [record]);

  // Sync the product search box with the selected product's name
  useEffect(() => {
    const selected = products.find(p => p.id === parseInt(formData.product_id));
    setProductSearch(selected ? `${selected.name} (${selected.item_code})` : '');
  }, [products, formData.product_id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await productsAPI.getAll({ limit: 1000, status: 'active' });
      setProducts(response.data.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    (product.item_code && product.item_code.toLowerCase().includes(productSearch.toLowerCase()))
  );

  const handleProductSelect = (product) => {
    setFormData(prev => ({ ...prev, product_id: product.id.toString() }));
    setProductSearch(`${product.name} (${product.item_code})`);
    setShowProductDropdown(false);
  };

  const handleClearProduct = () => {
    setFormData(prev => ({ ...prev, product_id: '' }));
    setProductSearch('');
    setShowProductDropdown(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.product_id) {
      toast.error('Please select a product');
      return;
    }
    if (!formData.quantity || parseInt(formData.quantity) < 1) {
      toast.error('Please enter a valid quantity');
      return;
    }
    if (!formData.unit_price || parseFloat(formData.unit_price) < 0) {
      toast.error('Please enter a valid unit price');
      return;
    }

    try {
      setLoading(true);
      if (record) {
        await stockInAPI.update(record.id, formData);
        toast.success('Stock-in record updated successfully');
      } else {
        await stockInAPI.create(formData);
        toast.success('Stock added successfully');
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving stock-in record:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">
            {record ? 'Edit Stock In' : 'Add Stock In'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6"  style={{height:"70vh",overflowY:"auto"}}>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Product */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product <span className="text-red-500">*</span>
                </label>
                <div className="relative" ref={productDropdownRef}>
                  {formData.product_id ? (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 px-4 py-2 border border-green-300 bg-green-50 rounded-lg text-sm truncate">
                        {productSearch}
                      </div>
                      <button
                        type="button"
                        onClick={handleClearProduct}
                        className="text-gray-400 hover:text-red-500 flex-shrink-0"
                        title="Change product"
                      >
                        <FiX size={18} />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="text"
                        value={productSearch}
                        onChange={(e) => {
                          setProductSearch(e.target.value);
                          setShowProductDropdown(true);
                        }}
                        onFocus={() => setShowProductDropdown(true)}
                        placeholder="Search product..."
                        className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required={!formData.product_id}
                      />
                    </div>
                  )}
                  {showProductDropdown && !formData.product_id && (
                    <div className="absolute z-30 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredProducts.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">No products found</div>
                      ) : (
                        filteredProducts.map((product) => (
                          <button
                            key={product.id}
                            type="button"
                            onClick={() => handleProductSelect(product)}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0"
                          >
                            {product.name} ({product.item_code})
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantity <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  min="1"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter quantity"
                  required
                />
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location <span className="text-red-500">*</span>
                </label>
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="Office">Office</option>
                  <option value="Godown">Godown</option>
                  <option value="Factory">Factory</option>
                </select>
                {/* Show current stock at selected location */}
                {formData.product_id && (
                  <div className="mt-1 text-sm text-gray-600">
                    Current Stock: {(() => {
                      const product = products.find(p => p.id === parseInt(formData.product_id));
                      if (!product) return 0;
                      if (formData.location === 'Office') return product.stock_office || 0;
                      if (formData.location === 'Godown') return product.stock_godown || 0;
                      if (formData.location === 'Factory') return product.stock_factory || 0;
                      return 0;
                    })()} {(() => {
                      const product = products.find(p => p.id === parseInt(formData.product_id));
                      return product ? product.unit : '';
                    })()}
                  </div>
                )}
              </div>

              {/* Unit Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Unit Price (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="unit_price"
                  value={formData.unit_price}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  required
                />
              </div>

              {/* Supplier Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Supplier Name
                </label>
                <input
                  type="text"
                  name="supplier_name"
                  value={formData.supplier_name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter supplier name"
                />
              </div>

              {/* Invoice Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Number
                </label>
                <input
                  type="text"
                  name="invoice_number"
                  value={formData.invoice_number}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter invoice number"
                />
              </div>

              {/* Invoice Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Date
                </label>
                <input
                  type="date"
                  name="invoice_date"
                  value={formData.invoice_date}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter any additional notes"
              />
            </div>

            {/* Stock Summary - Shows when product is selected */}
            {/* {formData.product_id && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-blue-800 mb-2">Stock Summary</h3>
                {(() => {
                  const product = products.find(p => p.id === parseInt(formData.product_id));
                  if (!product) return null;
                  const qty = parseInt(formData.quantity) || 0;
                  const currentStock = formData.location === 'Office' ? (product.stock_office || 0) :
                                       formData.location === 'Godown' ? (product.stock_godown || 0) :
                                       (product.stock_factory || 0);
                  const newStock = currentStock + qty;
                  return (
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Office:</span>
                        <span className={`ml-1 font-medium ${formData.location === 'Office' ? 'text-blue-600' : 'text-gray-800'}`}>
                          {formData.location === 'Office' ? `${product.stock_office || 0} → ${newStock}` : (product.stock_office || 0)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Godown:</span>
                        <span className={`ml-1 font-medium ${formData.location === 'Godown' ? 'text-blue-600' : 'text-gray-800'}`}>
                          {formData.location === 'Godown' ? `${product.stock_godown || 0} → ${newStock}` : (product.stock_godown || 0)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Factory:</span>
                        <span className={`ml-1 font-medium ${formData.location === 'Factory' ? 'text-blue-600' : 'text-gray-800'}`}>
                          {formData.location === 'Factory' ? `${product.stock_factory || 0} → ${newStock}` : (product.stock_factory || 0)}
                        </span>
                      </div>
                      <div className="col-span-3 pt-2 border-t border-blue-200">
                        <span className="text-gray-600">Total Stock:</span>
                        <span className="ml-1 font-semibold text-blue-800">
                          {product.total_stock || 0} → {(product.total_stock || 0) + qty}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )} */}
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Saving...' : record ? 'Update' : 'Add Stock'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StockInForm;
